import React from 'react';
import { TableRow, TableCell } from '@mui/material';
import { VisibilityChip } from './VisibilityChip';
import { SourceTypeChip } from './SourceTypeChip';
import { DocumentActionButtons } from './DocumentActionButtons';
import type { DocumentTableRowProps } from '../types';
import type { Document } from '../../../../api';

/**
 * Type guard to check if document has source_type field
 * SearchResult doesn't have source_type, but Document does
 */
function hasSourceType(doc: Document | any): doc is Document {
  return 'source_type' in doc;
}

/**
 * DocumentTableRow - Memoized table row component
 *
 * Only re-renders when its specific document changes.
 * Uses extracted cell components for clean separation of concerns.
 *
 * @param doc - Document to display
 * @param canEditDocument - Function to check if user can edit this document
 * @param canDeleteDocument - Function to check if user can delete this document
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 * @param onView - Callback when view button is clicked
 * @param onDownload - Callback when download button is clicked
 * @param renderOwner - Function to render owner cell
 * @param renderSharedGroups - Function to render shared groups cell
 * @param renderSharedUsers - Function to render shared users cell
 */
export const DocumentTableRow = React.memo<DocumentTableRowProps>(({
  doc,
  canEditDocument,
  canDeleteDocument,
  onEdit,
  onDelete,
  onView,
  onDownload,
  renderOwner,
  renderSharedGroups,
  renderSharedUsers
}) => {
  // Type-safe check for source_type field
  const sourceType = hasSourceType(doc) ? doc.source_type : undefined;
  const isSap = sourceType === 'sap';
  const canEdit = canEditDocument(doc);
  const canDelete = canDeleteDocument ? canDeleteDocument(doc) : canEdit;

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        {doc.title}
      </TableCell>
      <TableCell>
        <VisibilityChip visibility={doc.visibility || 'private'} />
      </TableCell>
      <TableCell>
        {renderOwner(doc)}
      </TableCell>
      <TableCell>
        {renderSharedGroups(doc)}
      </TableCell>
      <TableCell>
        {renderSharedUsers(doc)}
      </TableCell>
      <TableCell>
        <SourceTypeChip
          sourceType={sourceType}
          isSap={isSap}
        />
      </TableCell>
      <TableCell align="right">
        <DocumentActionButtons
          doc={doc}
          canEdit={canEdit}
          canDelete={canDelete}
          onView={onView}
          onDownload={onDownload}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </TableCell>
    </TableRow>
  );
});

DocumentTableRow.displayName = 'DocumentTableRow';
